import { Code } from '@zorro/design-system/v2/Code';
import { Box, Group, Text } from '@zorro/web/design-system';
import { useMemo } from 'react';

import { CopyIconButton } from '../CopyIconButton';

type Props = {
  value: unknown;
  label?: string;
  maxHeight?: number;
};

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
};

const isEmptyValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'object' && Object.keys(value as object).length === 0);

/**
 * Pretty-prints any JSON-serializable value with a copy button. Used for
 * workflow payloads and the deep error fields (originalError / itemData /
 * relatedIds).
 */
export function JsonViewer({ value, label, maxHeight = 400 }: Props) {
  const json = useMemo(() => safeStringify(value), [value]);
  const empty = isEmptyValue(value);

  return (
    <Box>
      <Group justify="space-between" mb="xs" wrap="nowrap">
        {label ? (
          <Text size="sm" fw={600}>
            {label}
          </Text>
        ) : (
          <span />
        )}
        {!empty && <CopyIconButton text={json} tooltip="Copy JSON" />}
      </Group>
      {empty ? (
        <Text size="sm" c="dimmed">
          No data
        </Text>
      ) : (
        <Code block mah={maxHeight}>
          {json}
        </Code>
      )}
    </Box>
  );
}
